<template>
  <LocationHelpModal v-if="locationHelpKind" :kind="locationHelpKind" @close="closeLocationHelp()" />
  <LocationAskModal
    v-if="locationAskReason"
    :reason="locationAskReason"
    @allow="allowLocationAsk"
    @dismiss="dismissLocationAsk"
  />
</template>

<script setup lang="ts">
import { GeoDataSource } from '@/core/data/location/location.interface';
import { isMobileOS } from '@/core/helpers/ui.helper';
import {
  APPROXIMATE_ACCURACY_M,
  APPROXIMATE_CONFIRM_MS,
  LocationHelpKind,
} from '@/views/composables/constants/main/locationHelp.constants';
import { allowLocationAsk, dismissLocationAsk, locationAskReason } from '@/views/composables/hooks/useLocationAsk';
import {
  closeLocationHelp,
  locationHelpKind,
  notifyLocationApproximate,
} from '@/views/composables/hooks/useLocationHelp';
import LocationAskModal from '@/views/main/locations/modals/LocationAskModal.vue';
import LocationHelpModal from '@/views/main/locations/modals/LocationHelpModal.vue';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useGeolocationStore } from './stores/geolocation.store';

const geolocationStore = useGeolocationStore();
const { isLocationPermissionGranted, isLocationPermissionRequested, userLocation } = storeToRefs(geolocationStore);
const route = useRoute();

watch([isLocationPermissionGranted, isLocationPermissionRequested], ([isGranted, isRequested]) => {
  if (!isGranted && isRequested) {
    geolocationStore.requestLocationPermission();
  }
});

// If the browser reports the grant live (mostly Chrome/Android), the blocked
// help is done — no reload needed in that case.
watch(isLocationPermissionGranted, (isGranted) => {
  if (isGranted) {
    closeLocationHelp([LocationHelpKind.BLOCKED]);
  }
});

watch(
  () => route.name,
  () => closeLocationHelp(),
);

let firstCoarseFixAt: number | null = null;

watch(
  () => (userLocation.value?.source === GeoDataSource.BROWSER_API ? userLocation.value.updatedAt : null),
  (fixAt) => {
    if (!fixAt) return;

    // A real GPS fix makes the blocked and no-position help obsolete.
    closeLocationHelp([LocationHelpKind.BLOCKED, LocationHelpKind.UNAVAILABLE]);

    const accuracy = userLocation.value?.accuracy ?? 0;
    if (accuracy <= APPROXIMATE_ACCURACY_M) {
      firstCoarseFixAt = null;
      closeLocationHelp([LocationHelpKind.APPROXIMATE]);
      return;
    }

    if (!isMobileOS.value) return;

    const now = Date.now();
    if (firstCoarseFixAt === null) {
      firstCoarseFixAt = now;
      return;
    }
    if (now - firstCoarseFixAt >= APPROXIMATE_CONFIRM_MS) {
      notifyLocationApproximate();
    }
  },
);
</script>
